import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { FeatureAnimation, FEATURE_ANIMATIONS } from './FeatureAnimations';

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const controls = useAnimation();

  const features = useMemo(() => [
    {
      id: 1,
      title: "SKU-level Price Elasticity",
      description: "Price each product based on what actually drives margin, not market guesswork.",
      icon: "bi bi-currency-euro",
      color: "#3d5a80",
      gradient: "linear-gradient(135deg, #3d5a80 0%, #98c1d9 100%)",
      particles: 40,
      isHidden: false,
      animationType: FEATURE_ANIMATIONS.PRICE_ELASTICITY
    },
    {
      id: 2,
      title: 'StrataMemory™',
      description: 'Our AI remembers what worked, and why, so pricing keeps getting smarter.',
      icon: 'bi bi-cpu',
      color: "#ee6c4d",
      gradient: "linear-gradient(135deg, #ee6c4d 0%, #3d5a80 100%)",
      particles: 30,
      isHidden: false,
      animationType: FEATURE_ANIMATIONS.STRATA_MEMORY
    },
    {
      id: 3,
      title: 'Predictive Analytics',
      description: 'Predict outcomes before changes go live. No surprises. Just profit.',
      icon: 'bi bi-graph-up',
      color: "#293241",
      gradient: "linear-gradient(135deg, #293241 0%, #3d5a80 100%)",
      particles: 40,
      isHidden: false,
      animationType: FEATURE_ANIMATIONS.PREDICTIVE_ANALYTICS
    },
    {
      id: 4,
      title: 'AI-Powered Insights',
      description: 'Interact with AI for real-time recommendations and proactive suggestions to optimize your strategies.',
      icon: 'bi bi-lightning-charge',
      color: "#98c1d9",
      gradient: "linear-gradient(135deg, #98c1d9 0%, #ee6c4d 100%)",
      particles: 35,
      isHidden: true,
      animationType: FEATURE_ANIMATIONS.AI_INSIGHTS
    },
  ], []);

  const visibleFeatures = useMemo(() => features.filter(feature => !feature.isHidden), [features]);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % visibleFeatures.length);
    }, 10000); // Interval 10 seconds
    
    return () => clearInterval(interval);
  }, [visibleFeatures.length]);

  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, [activeFeature, controls]);

  // Generate particles
  const generateParticles = (count, color) => {
    return Array.from({ length: count }).map((_, index) => ({
      id: index,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
      color
    }));
  };

  // Card animation variants
  const cardVariants = {
    inactive: { 
      scale: 0.95, 
      opacity: 0.7,
      y: 20,
      transition: { duration: 0.5 }
    },
    active: { 
      scale: 1, 
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, type: "spring", stiffness: 300, damping: 24 }
    }
  };

  // Particle animation component
  const ParticleField = ({ color, count }) => {
    const particles = generateParticles(count, color);
    
    return (
      <div className="particle-container">
        {particles.map(particle => (
          <motion.div
            key={particle.id}
            className="particle"
            initial={{ 
              x: `${particle.x}%`, 
              y: `${particle.y}%`, 
              opacity: 0 
            }}
            animate={{ 
              x: [`${particle.x}%`, `${(particle.x + 20) % 100}%`, `${(particle.x - 10) % 100}%`],
              y: [`${particle.y}%`, `${(particle.y - 20) % 100}%`, `${(particle.y + 15) % 100}%`],
              opacity: [0, 0.8, 0]
            }}
            transition={{ 
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              repeatType: "reverse"
            }}
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              borderRadius: '50%',
              position: 'absolute'
            }}
          />
        ))}
      </div>
    );
  };
    
  return (
    <section className="features-section py-5" style={{ background: '#f8f9fa', position: 'relative', overflow: 'hidden' }}>
      {/* Background grid and glow effect */}
      <div className="features-bg-grid"></div>
      <div className="features-glow" style={{ 
        background: visibleFeatures[activeFeature]?.gradient || features[0].gradient,
        opacity: 0.05,
        filter: 'blur(150px)',
        position: 'absolute',
        width: '60%',
        height: '60%',
        top: '20%',
        left: '20%',
        borderRadius: '50%',
        zIndex: 0
      }}></div>
      
      <Container className="position-relative" style={{ zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-5"
        >
          <h2>Every SKU, every margin. <span style={{ color: 'var(--ai-teal)' }}>Optimized.</span></h2>
          <div className="title-underline mx-auto"></div>
          <p className="modern-paragraph">Discover how our SKU-level pricing analysis can transform your margin performance</p>
        </motion.div>

        <Row className="align-items-center">
          {/* Left column - Animation */}
          <Col lg={6} className="position-relative">
            <div className="animation-container" style={{ height: '500px', position: 'relative' }}>
              <AnimatePresence mode="wait">
                {visibleFeatures.map((feature, index) => (
                  activeFeature === index && (
                    <motion.div
                      key={`animation-${feature.id}`}
                      className="animation-wrapper"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.6 }}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        background: 'rgba(224, 251, 252, 0.3)',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        border: '1px solid rgba(61, 90, 128, 0.12)'
                      }}
                    >
                      {/* Particle animation in background */}
                      <ParticleField color={feature.color} count={feature.particles} />
                      
                      {/* Modular Feature Animation */}
                      <FeatureAnimation 
                        feature={feature} 
                        animationType={feature.animationType} 
                      />
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          </Col>
          
          {/* Right column - Stacked Feature Cards */}
          <Col lg={6}>
            <div className="feature-cards-stack">
              {visibleFeatures.map((feature, index) => (
                <motion.div
                  key={`card-${feature.id}`}
                  className={`feature-card ${activeFeature === index ? 'active' : ''}`}
                  variants={cardVariants}
                  initial="inactive"
                  animate={activeFeature === index ? "active" : "inactive"}
                  onClick={() => setActiveFeature(index)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(15px)',
                    borderRadius: '16px',
                    border: `1px solid rgba(${feature.color.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, ${activeFeature === index ? 0.5 : 0.15})`,
                    boxShadow: activeFeature === index ? 
                      `0 10px 30px rgba(61, 90, 128, 0.15), 0 0 30px rgba(${feature.color.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` : 
                      '0 4px 15px rgba(61, 90, 128, 0.06)',
                    padding: '16px 20px',
                    marginBottom: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div className="card-content">
                    <div className="feature-header" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px' 
                    }}>
                      <i 
                        className={feature.icon} 
                        style={{ 
                          fontSize: '1.5rem', 
                          marginRight: '15px',
                          background: feature.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      ></i>
                      <h3 style={{ 
                        fontSize: '1.6rem',
                        margin: 0,
                        background: feature.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}>{feature.title}</h3>
                    </div>
                    
                    <p style={{ 
                      fontSize: '1rem', 
                      lineHeight: '1.5', 
                      color: activeFeature === index ? '#293241' : 'rgba(41, 50, 65, 0.7)',
                      margin: 0
                    }}>
                      {feature.description}
                    </p>
                  </div>
                  
                  {/* Highlight bar */}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    background: activeFeature === index ? feature.gradient : 'transparent',
                    borderRadius: '3px 0 0 3px'
                  }}></div>
                </motion.div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
      
      <style jsx>{`
        .features-section {
          position: relative;
          overflow: hidden;
          padding: 60px 0;
        }
        
        .features-bg-grid {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: linear-gradient(rgba(61,90,128,0.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(61,90,128,0.04) 1px, transparent 1px);
          background-size: 40px 40px;
          z-index: 0;
        }
        
        .particle-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        
        .feature-card {
          transform-origin: center center;
        }
        
        .feature-card.active {
          z-index: 2;
        }
        
        .feature-cards-stack {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
      `}</style>
    </section>
  );
};

export default Features;
